import React, { useEffect, useMemo, useRef, useState } from 'react'
import apiClient, { API_ENDPOINTS, BASE_URL, getUserInfoSync } from '../../config/api'
import { usePrintReactComponent } from '../../hooks/usePrintReactComponent'

const todayISO = () => new Date().toISOString().split('T')[0]

const getPreferredBranchId = (userInfo) => {
  if (!userInfo) return null
  if (typeof userInfo.branch === 'number') return userInfo.branch
  if (typeof userInfo.branch === 'object' && userInfo.branch?.id) return userInfo.branch.id
  return (
    userInfo.branch_id ||
    userInfo.assigned_branch_id ||
    userInfo.branchId ||
    null
  )
}

const CashReceivedSummary = () => {
  const userInfo = getUserInfoSync()
  const preferredBranchId = useMemo(() => getPreferredBranchId(userInfo), [userInfo])

  const [cashiers, setCashiers] = useState([])
  const [cashierError, setCashierError] = useState('')

  const [entries, setEntries] = useState([])
  const [summaryData, setSummaryData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [datePreset, setDatePreset] = useState('today')
  const [serverFilters, setServerFilters] = useState({
    startDate: todayISO(),
    endDate: todayISO()
  })

  const [localFilters, setLocalFilters] = useState({
    search: '',
    cashierId: '',
    branchName: ''
  })

  const reportRef = useRef(null)
  const { htmlToPdf, isPrinting, error: printError } = usePrintReactComponent()

  const fetchCashiers = async () => {
    try {
      setCashierError('')
      const response = await apiClient.get('/api/accounts/users/')
      const raw = Array.isArray(response.data) ? response.data : response.data?.results || []
      const onlyCashiers = raw
        .filter((user) => user?.user?.role === 'cashier')
        .map((user) => {
          const displayName = `${user.first_name || ''} ${user.last_name || ''}`.trim()
          const employeeName = user.user?.employee_name
          const fallbackName = user.user?.email?.split('@')?.[0]
          return {
            id: user.id || user.user?.id,
            userId: user.user?.id,
            name: displayName || employeeName || fallbackName || 'Cashier',
            email: user.user?.email || '—'
          }
        })
      setCashiers(onlyCashiers)
    } catch (err) {
      console.error('Failed to load cashiers:', err)
      const apiError = err.response?.data
      setCashiers([])
      setCashierError(
        apiError?.detail ||
        apiError?.message ||
        'Unable to load cashiers. Cashier filter may be incomplete.'
      )
    }
  }

  const fetchSummary = async (filters = serverFilters) => {
    try {
      console.log('Fetching summary without params (using default date range)')
      const response = await apiClient.get(`${BASE_URL}/api/sales/cash-received/summary/`)
      console.log('Summary API response:', response)
      console.log('Summary API response.data:', response.data)
      
      if (response.data) {
        setSummaryData(response.data)
        console.log('Summary data set:', response.data)
      } else {
        console.warn('No data in summary response')
        setSummaryData(null)
      }
    } catch (err) {
      console.error('Failed to load cash received summary:', err)
      console.error('Error details:', err.response?.data || err.message)
      setSummaryData(null)
    }
  }

  const fetchEntries = async (filters = serverFilters) => {
    try {
      setLoading(true)
      setError('')

      const activeFilters = filters || serverFilters

      const params = {
        start_date: activeFilters.startDate || undefined,
        end_date: activeFilters.endDate || undefined,
        branch: preferredBranchId || undefined
      }

      const response = await apiClient.get(API_ENDPOINTS.SALES_CASH_RECEIVED, { params })
      const data = Array.isArray(response.data) ? response.data : response.data?.results || []
      setEntries(data)
      
      // Also fetch summary data
      await fetchSummary(activeFilters)
    } catch (err) {
      console.error('Failed to load cash received summary:', err)
      const apiError = err.response?.data
      setEntries([])
      setError(
        apiError?.detail ||
        apiError?.message ||
        'Unable to load cash received summary. Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const today = todayISO()
    fetchEntries({ startDate: today, endDate: today })
    fetchCashiers()
    // We intentionally don't add fetchEntries/fetchCashiers to deps to avoid double initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    console.log('summaryData state changed:', summaryData)
  }, [summaryData])

  const handleServerFilterChange = (event) => {
    const { name, value } = event.target
    setServerFilters((prev) => ({
      ...prev,
      [name]: value
    }))
  }

  const handleLocalFilterChange = (event) => {
    const { name, value } = event.target
    setLocalFilters((prev) => ({
      ...prev,
      [name]: value
    }))
  }

  const handlePresetChange = (preset) => {
    setDatePreset(preset)

    const today = new Date()
    let start
    let end

    if (preset === 'today') {
      start = todayISO()
      end = todayISO()
    } else if (preset === 'week') {
      const day = today.getDay() || 7 // Make Sunday = 7
      const monday = new Date(today)
      monday.setDate(today.getDate() - (day - 1))
      start = monday.toISOString().split('T')[0]
      end = todayISO()
    } else if (preset === 'month') {
      const first = new Date(today.getFullYear(), today.getMonth(), 1)
      start = first.toISOString().split('T')[0]
      end = todayISO()
    } else {
      // custom – don't auto-change dates here
      return
    }

    const nextFilters = {
      startDate: start,
      endDate: end
    }
    setServerFilters(nextFilters)
    fetchEntries(nextFilters)
  }

  const handleApplyFilters = (event) => {
    event.preventDefault()
    setDatePreset('custom')
    fetchEntries({
      startDate: serverFilters.startDate,
      endDate: serverFilters.endDate
    })
  }

  const handleResetFilters = () => {
    const today = todayISO()
    setDatePreset('today')
    setServerFilters({
      startDate: today,
      endDate: today
    })
    setLocalFilters({
      search: '',
      cashierId: '',
      branchName: ''
    })
    fetchEntries({ startDate: today, endDate: today })
  }

  const uniqueBranches = useMemo(() => {
    const set = new Set()
    entries.forEach((entry) => {
      if (entry.branch_name) {
        set.add(entry.branch_name)
      }
    })
    return Array.from(set)
  }, [entries])

  const filteredEntries = useMemo(() => {
    const search = localFilters.search.trim().toLowerCase()

    return entries.filter((entry) => {
      if (localFilters.cashierId && String(entry.cashier) !== localFilters.cashierId) {
        return false
      }

      if (localFilters.branchName && entry.branch_name !== localFilters.branchName) {
        return false
      }

      if (search) {
        const haystack = [
          entry.cashier_name,
          entry.cashier_email,
          entry.branch_name,
          entry.notes,
          entry.date,
          entry.type_of_payment,
          entry.type_of_payment_display
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        if (!haystack.includes(search)) {
          return false
        }
      }

      return true
    })
  }, [entries, localFilters])

  const summary = useMemo(() => {
    let totalAmount = 0
    const byCashier = {}

    filteredEntries.forEach((entry) => {
      const amount = Number(entry.total_amount || 0)
      totalAmount += amount

      const id = entry.cashier
      if (!id) return

      if (!byCashier[id]) {
        byCashier[id] = {
          cashierId: id,
          cashierName: entry.cashier_name || 'Cashier',
          branchName: entry.branch_name || '',
          totalAmount: 0,
          count: 0
        }
      }

      byCashier[id].totalAmount += amount
      byCashier[id].count += 1
    })

    return {
      totalEntries: filteredEntries.length,
      totalAmount,
      byCashier: Object.values(byCashier)
    }
  }, [filteredEntries])

  const handleDownloadPdf = async () => {
    if (!reportRef.current) return

    try {
      const pdfBlob = await htmlToPdf(reportRef.current, {
        scale: 2,
        backgroundColor: '#ffffff'
      })

      const url = URL.createObjectURL(pdfBlob)
      const link = document.createElement('a')
      const from = serverFilters.startDate || 'from'
      const to = serverFilters.endDate || 'to'
      link.href = url
      link.download = `cash-received-${from}-to-${to}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to generate PDF:', err)
    }
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Cash Received Summary</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/branch-manager">Home</a></li>
            <li className="breadcrumb-item">Sales</li>
            <li className="breadcrumb-item active">Cash Received Summary</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row gy-4">
          <div className="col-12">
            <div className="card">
              <div className="card-body" ref={reportRef}>
                <div className="d-flex flex-column flex-xl-row justify-content-between align-items-start align-items-xl-center gap-3 mb-4">
                  <div>
                    <h5 className="card-title mb-1">Advanced Filters</h5>
                    <p className="text-muted small mb-0">
                      Analyse cash received by date range, cashier, branch, or keyword search.
                    </p>
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={fetchEntries}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                          Refreshing…
                        </>
                      ) : (
                        <>
                          <i className="bi bi-arrow-clockwise me-1" />
                          Refresh
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleDownloadPdf}
                      disabled={loading || isPrinting || filteredEntries.length === 0}
                    >
                      {isPrinting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                          Preparing PDF…
                        </>
                      ) : (
                        <>
                          <i className="bi bi-file-earmark-pdf me-1" />
                          Download PDF
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="btn-group" role="group" aria-label="Date range presets">
                    <button
                      type="button"
                      className={`btn btn-sm ${datePreset === 'today' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => handlePresetChange('today')}
                    >
                      Today
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${datePreset === 'week' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => handlePresetChange('week')}
                    >
                      This week
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${datePreset === 'month' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => handlePresetChange('month')}
                    >
                      This month
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${datePreset === 'custom' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setDatePreset('custom')}
                    >
                      Custom
                    </button>
                  </div>
                  <p className="text-muted small mb-0 mt-1">
                    Showing: {serverFilters.startDate} to {serverFilters.endDate} ({datePreset})
                  </p>
                </div>

                <form className="row g-3 mb-3" onSubmit={handleApplyFilters}>
                  <div className="col-md-3">
                    <label className="form-label">From date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="startDate"
                      value={serverFilters.startDate}
                      onChange={handleServerFilterChange}
                      max={serverFilters.endDate || todayISO()}
                      disabled={datePreset !== 'custom'}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">To date</label>
                    <input
                      type="date"
                      className="form-control"
                      name="endDate"
                      value={serverFilters.endDate}
                      onChange={handleServerFilterChange}
                      min={serverFilters.startDate}
                      max={todayISO()}
                      disabled={datePreset !== 'custom'}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Cashier</label>
                    <select
                      className="form-select"
                      name="cashierId"
                      value={localFilters.cashierId}
                      onChange={handleLocalFilterChange}
                    >
                      <option value="">All cashiers</option>
                      {cashiers.map((cashier) => (
                        <option key={cashier.id} value={String(cashier.id)}>
                          {cashier.name} {cashier.email ? `(${cashier.email})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Branch</label>
                    <select
                      className="form-select"
                      name="branchName"
                      value={localFilters.branchName}
                      onChange={handleLocalFilterChange}
                    >
                      <option value="">All branches</option>
                      {uniqueBranches.map((branch) => (
                        <option key={branch} value={branch}>
                          {branch}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Search</label>
                    <input
                      type="search"
                      className="form-control"
                      name="search"
                      placeholder="Search by cashier, email, branch, payment type, notes, or date"
                      value={localFilters.search}
                      onChange={handleLocalFilterChange}
                    />
                  </div>
                  <div className="col-md-6 d-flex align-items-end justify-content-md-end gap-2">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                          Applying…
                        </>
                      ) : (
                        'Apply date filters'
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleResetFilters}
                      disabled={loading}
                    >
                      Clear all
                    </button>
                  </div>
                </form>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                {cashierError && (
                  <div className="alert alert-warning" role="alert">
                    {cashierError}
                  </div>
                )}

                {printError && (
                  <div className="alert alert-warning" role="alert">
                    {printError}
                  </div>
                )}

                {!error && (() => {
                  console.log('Rendering summary cards - summaryData:', summaryData)
                  console.log('Rendering summary cards - summary.totalEntries:', summary.totalEntries)
                  console.log('Rendering summary cards - grand_total_usd:', summaryData?.grand_total_usd)
                  console.log('Rendering summary cards - total_cash_received_usd:', summaryData?.total_cash_received_usd)
                  return (
                    <div className="row g-3 mb-3">
                      <div className="col-md-4">
                        <div className="border rounded-3 p-3 h-100">
                          <p className="text-uppercase text-muted small mb-1">Total entries</p>
                          <h4 className="mb-0">{summaryData?.total_entries ?? summary.totalEntries}</h4>
                          <small className="text-muted">
                            Between {serverFilters.startDate || '—'} and {serverFilters.endDate || '—'}
                          </small>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="border rounded-3 p-3 h-100">
                          <p className="text-uppercase text-muted small mb-1">Total cash received</p>
                          <h4 className="mb-0">
                            $
                            {Number(summaryData?.grand_total_usd ?? summaryData?.total_cash_received_usd ?? 0).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </h4>
                          {summaryData && (
                            <div className="mt-2">
                              <small className="text-muted d-block">
                                USD: ${Number(summaryData.total_cash_received_usd || 0).toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}
                              </small>
                              <small className="text-muted d-block">
                                ZIG: {Number(summaryData.total_cash_received_zig || 0).toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })} (${Number(summaryData.total_cash_received_zig_usd || 0).toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })})
                              </small>
                            </div>
                          )}
                          {!summaryData && (
                            <small className="text-muted">After local filters are applied</small>
                          )}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="border rounded-3 p-3 h-100">
                          <p className="text-uppercase text-muted small mb-1">Branch filter</p>
                          <h4 className="mb-0">
                            {localFilters.branchName || 'All branches'}
                          </h4>
                          <small className="text-muted">
                            Default branch:{' '}
                            {preferredBranchId ? `#${preferredBranchId}` : 'Not set'}
                          </small>
                        </div>
                      </div>
                    </div>
                  )
                })()}

                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" aria-hidden="true" />
                    <p className="text-muted mt-3 mb-0">Loading cash received entries…</p>
                  </div>
                ) : filteredEntries.length === 0 ? (
                  <div className="alert alert-light border mb-0" role="alert">
                    No cash received entries match the selected filters.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle">
                      <thead>
                        <tr>
                          <th scope="col">Date</th>
                          <th scope="col">Cashier</th>
                          <th scope="col">Branch</th>
                          <th scope="col">Type of Payment</th>
                          <th scope="col" className="text-end">Amount</th>
                          <th scope="col">Notes</th>
                          <th scope="col">Captured by</th>
                          <th scope="col">Captured at</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEntries.map((entry) => (
                          <tr key={entry.id}>
                            <td>
                              <div className="fw-semibold">{entry.date}</div>
                              <div className="text-muted small">
                                #{entry.id}
                              </div>
                            </td>
                            <td>
                              <div className="fw-semibold">{entry.cashier_name || 'Cashier'}</div>
                              <div className="text-muted small">{entry.cashier_email}</div>
                            </td>
                            <td>{entry.branch_name || '—'}</td>
                            <td>
                              <span className="badge bg-primary">
                                {entry.type_of_payment_display || entry.type_of_payment || '—'}
                              </span>
                            </td>
                            <td className="text-end">
                              ${Number(entry.total_amount || 0).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </td>
                            <td>
                              {entry.notes ? (
                                <span className="text-wrap d-inline-block" style={{ maxWidth: '260px' }}>
                                  {entry.notes}
                                </span>
                              ) : (
                                <span className="text-muted small">No notes</span>
                              )}
                            </td>
                            <td>
                              <div className="text-muted small">{entry.entered_by_email || '—'}</div>
                            </td>
                            <td>
                              <div className="text-muted small">
                                {entry.created_at
                                  ? new Date(entry.created_at).toLocaleString()
                                  : '—'}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {(() => {
                  const cashierBreakdown = summaryData?.breakdown_by_cashier || summary.byCashier || []
                  const hasData = Array.isArray(cashierBreakdown) && cashierBreakdown.length > 0
                  
                  if (!hasData) return null
                  
                  console.log('Rendering breakdown by cashier:', cashierBreakdown)
                  
                  return (
                    <>
                      <hr className="my-4" />
                      <h6 className="mb-3">Breakdown by cashier</h6>
                      <div className="row g-3">
                        {cashierBreakdown.map((item) => {
                          // Handle API data structure
                          const cashierId = item.cashier_id || item.cashierId
                          const cashierName = item.cashier_name || item.cashierName || 'Cashier'
                          const cashierEmail = item.cashier_email || item.cashierEmail || ''
                          const grandTotal = item.grand_total_usd || item.totalAmount || 0
                          const totalUsd = item.total_usd || 0
                          const totalZig = item.total_zig || 0
                          const totalZigUsd = item.total_zig_usd || 0
                          const entryCount = item.entry_count || item.count || 0
                          
                          return (
                            <div key={cashierId} className="col-md-4 col-lg-3">
                              <div className="border rounded-3 p-3 h-100">
                                <p className="fw-semibold mb-1">{cashierName}</p>
                                {cashierEmail && (
                                  <p className="text-muted small mb-2">{cashierEmail}</p>
                                )}
                                <p className="mb-1">
                                  <strong>
                                    $
                                    {Number(grandTotal).toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2
                                    })}
                                  </strong>
                                </p>
                                {(totalUsd > 0 || totalZig > 0) && (
                                  <div className="mb-2">
                                    {totalUsd > 0 && (
                                      <small className="text-muted d-block">
                                        USD: ${Number(totalUsd).toLocaleString(undefined, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2
                                        })}
                                      </small>
                                    )}
                                    {totalZig > 0 && (
                                      <small className="text-muted d-block">
                                        ZIG: {Number(totalZig).toLocaleString(undefined, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2
                                        })} (${Number(totalZigUsd).toLocaleString(undefined, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2
                                        })})
                                      </small>
                                    )}
                                  </div>
                                )}
                                <p className="text-muted small mb-0">
                                  {entryCount} entr{entryCount === 1 ? 'y' : 'ies'} in range
                                </p>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </>
                  )
                })()}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default CashReceivedSummary

